import {
  applyTeamSetup,
  MATCH_MODE_MENU_IDS,
  MATCH_MODES,
  setMatchMode,
  type AssistLevel,
  type MatchMode,
  type MatchModeId,
  type PitchSkinId,
} from "@golazo/shared";
import "./menu.css";
import { t } from "../i18n/index.js";
import { bindMenuAmbience, MENU_BACKDROP_HTML } from "./menuAmbience.js";
import { applyTeamPlan, createCustomize } from "./customize.js";
import {
  DIFFICULTIES,
  DIFFICULTY_BLURB,
  DIFFICULTY_HINT,
  DIFFICULTY_LABEL,
  loadDifficulty,
  saveDifficulty,
  type Difficulty,
} from "../game/difficulty.js";
import { LOCAL_PLAYERS, secondSeat, type LocalPlayers } from "../game/localPlayers.js";
import { localPairAvailable } from "../mobile/platform.js";

export interface MenuResult {
  modeId: MatchModeId;
  shirtNumber: number;
  assist: AssistLevel;
  pitchSkin: PitchSkinId;
  /** Apodo elegido en el modal. Vacio = sin nombre sobre el jugador. */
  nickname: string;
  /** Lo buena que es la IA (los niveles de la carrera). */
  difficulty: Difficulty;
  /** Cuantos juegan en este ordenador. Ver `game/localPlayers.ts`. */
  players: LocalPlayers;
}

export interface MenuHandle {
  /**
   * Igual que el lobby: `{ back: true }` cuando se vuelve sin elegir, y
   * `{ career: true }` cuando se entra en la carrera.
   */
  show(): Promise<MenuPick>;
  hide(): void;
  /** Salir porque se va a otro sitio desde fuera (un aviso de un amigo). */
  leave(): void;
  dispose(): void;
}

export type MenuPick = MenuResult | { back: true } | { career: true };

export interface MenuOptions {
  /** Donde va tu carrera ("Ytuano FC · Jornada 3"), para la tarjeta de entrada. */
  careerTeaser?: () => string;
  /**
   * Dificultad si nunca se ha elegido una. La decide quien conoce al jugador:
   * a quien no ha jugado nunca contra la IA, el Barrio; a quien ya jugaba,
   * Primera, que es la de siempre (no se le cambia el juego sin avisar).
   */
  defaultDifficulty?: () => Difficulty;
}

/** Una bota con sus tacos: la carrera es la tuya, no la de un equipo. */
const CAREER_ICON = `
<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <path d="M7 4h4.5v6.2c0 .9.6 1.6 1.4 1.8l4.3 1.1c1.6.4 2.8 1.9 2.8 3.6V18H3.5v-2.4c0-.9.4-1.7 1-2.3L7 11V4Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
  <path d="M5 21v-3M9.5 21v-3M14 21v-3M18.5 21v-3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
  <path d="M8 7h3M8 9.5h3" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" opacity="0.6"/>
</svg>`.trim();

/**
 * Lo que va bajo el nombre del modo.
 *
 * Antes repetia el propio nombre — la pastilla ponia "3V3" y debajo "3v3" —,
 * asi que la segunda linea no informaba de nada. Ahora dice cuanta gente cabe,
 * contando solo jugadores de campo (el portero lo lleva la IA), que es la
 * misma cuenta que ensena el lobby online.
 */
function playersLabel(mode: MatchMode): string {
  const outfield = Math.max(0, mode.formation.length - 1) * 2;
  return t("{n} jug.", { n: outfield });
}

function pitchDots(mode: MatchMode): string {
  const home = mode.formation
    .map((spec) => {
      const left = spec.depth * 46 + 4;
      const top = spec.lane * 100;
      const gk = spec.role === "gk" ? " is-gk" : "";
      return `<i class="mode-dot is-home${gk}" style="left:${left}%;top:${top}%"></i>`;
    })
    .join("");

  const away = mode.formation
    .map((spec) => {
      const left = 100 - (spec.depth * 46 + 4);
      const top = spec.lane * 100;
      const gk = spec.role === "gk" ? " is-gk" : "";
      return `<i class="mode-dot is-away${gk}" style="left:${left}%;top:${top}%"></i>`;
    })
    .join("");

  return home + away;
}










export function createMenu(root: HTMLElement = document.body, options: MenuOptions = {}): MenuHandle {
  const overlay = document.createElement("div");
  overlay.id = "menu";
  overlay.className = "hub-overlay hub-overlay--scroll hub-overlay--setup";
  overlay.innerHTML = `
    ${MENU_BACKDROP_HTML}

    <!--
      Volver, fuera de .menu-shell a proposito: su animacion de entrada acaba
      en translateY(0), que sigue siendo un transform, y un transform en un
      ancestro convierte el "position: fixed" en relativo a el. Dentro, el
      boton se quedaba junto al titulo en vez de en la esquina.
    -->
    <button type="button" class="lobby-back lobby-back--ghost match-back" data-back>${t("← Menú")}</button>

    <div class="menu-shell match-setup">
      <header class="match-header">
        <h1>FYNTA</h1>
        <p>${t("elige tu partido")}</p>
      </header>

      <!--
        La carrera entra por aqui, no por el menu principal: es la forma larga
        de jugar contra la IA, y el partido rapido de debajo la corta. Arriba y
        en dorado para que no pase por un ajuste mas.
      -->
      <button type="button" class="menu-career" data-career-mode>
        <span class="menu-career-icon">${CAREER_ICON}</span>
        <span class="menu-career-body">
          <strong>${t("CARRERA")}</strong>
          <span class="menu-career-teaser" data-season-teaser></span>
        </span>
        <span class="menu-career-go" aria-hidden="true">›</span>
      </button>

      <div class="setup-card">
        <section class="setup-block" aria-label="${t("Modo de juego")}">
          <h2 class="setup-label">${t("Modo")}</h2>
          <div class="mode-strip" role="listbox" aria-label="${t("Seleccionar modo")}"></div>
          <div class="mode-preview">
            <div class="mode-pitch mode-pitch--hero" data-mode-preview aria-hidden="true"></div>
            <p class="mode-preview-meta" data-mode-meta></p>
          </div>
        </section>

        <!--
          La dificultad, con los nombres de la carrera: jugar "en Regional" es
          jugar contra la IA de un club de Regional. Existe porque, medido, la
          gente nueva perdia 9 de cada 10 contra la IA de siempre.
        -->
        <section class="setup-block setup-block--difficulty" aria-label="${t("Dificultad")}">
          <h2 class="setup-label">${t("Dificultad")}</h2>
          <div class="diff-strip" role="radiogroup" aria-label="${t("Dificultad")}">
            ${DIFFICULTIES.map(
              (d) => `<button type="button" class="mode-pill diff-pill" role="radio" data-diff="${d}"><strong>${t(DIFFICULTY_LABEL[d])}</strong><span>${t(DIFFICULTY_HINT[d])}</span></button>`,
            ).join("")}
          </div>
          <p class="mode-preview-meta diff-blurb" data-diff-blurb></p>
        </section>

        <!--
          Dos en el mismo ordenador. El menu lo prometia desde el principio y no
          existia. Solo en el navegador y con teclado (ver
          \`localPairAvailable\`). Una sola fila: el menu no tiene scroll y un
          bloque como el de la dificultad lo desbordaba 76-114 px; las teclas de
          cada uno van en la linea de controles del pie, que ya existia.
        -->
        <section class="setup-block setup-block--players" data-players-block aria-label="${t("Jugadores")}">
          <h2 class="setup-label">${t("Jugadores")}</h2>
          <div class="players-strip" role="radiogroup" aria-label="${t("Jugadores")}">
            <button type="button" class="mode-pill players-pill" role="radio" data-players="solo"><strong>1</strong><span>${t("Contra la IA")}</span></button>
            <button type="button" class="mode-pill players-pill" role="radio" data-players="together"><strong>2</strong><span>${t("Juntos")}</span></button>
            <button type="button" class="mode-pill players-pill" role="radio" data-players="versus"><strong>2</strong><span>${t("Rivales")}</span></button>
          </div>
        </section>

      </div>

      <footer class="setup-footer">
        <div class="setup-actions">
          <button type="button" class="menu-customize" data-customize>${t("Personalizar")}</button>
          <button type="button" class="menu-play menu-play--wide">${t("Jugar")}</button>
        </div>
        <div class="menu-controls menu-controls--compact" aria-hidden="true" data-solo-controls>
          <span><kbd>←</kbd><kbd>→</kbd> ${t("modo")}</span>
          <span><kbd>Enter</kbd> ${t("jugar")}</span>
          <span><kbd>Esc</kbd> ${t("volver")}</span>
        </div>
        <div class="menu-controls menu-controls--compact menu-controls--pair" data-pair-controls hidden>
          <span><strong>${t("Jugador 1")}</strong> <kbd>WASD</kbd> <kbd>${t("Espacio")}</kbd> <kbd>⇧ ${t("izq.")}</kbd></span>
          <span><strong>${t("Jugador 2")}</strong> <kbd>← ↑ → ↓</kbd> <kbd>Enter</kbd> <kbd>⇧ ${t("der.")}</kbd></span>
          <span>${t("o un mando cada uno")}</span>
        </div>
      </footer>
    </div>
  `;

  root.appendChild(overlay);

  const modeStrip = overlay.querySelector(".mode-strip") as HTMLElement;
  const previewPitch = overlay.querySelector("[data-mode-preview]") as HTMLElement;
  const previewMeta = overlay.querySelector("[data-mode-meta]") as HTMLElement;
  const playBtn = overlay.querySelector(".menu-play") as HTMLButtonElement;
  const backBtn = overlay.querySelector("[data-back]") as HTMLButtonElement;
  backBtn.addEventListener("click", goBack);
  const customizeBtn = overlay.querySelector("[data-customize]") as HTMLButtonElement;
  const careerBtn = overlay.querySelector("[data-career-mode]") as HTMLButtonElement;
  const careerTeaser = overlay.querySelector("[data-season-teaser]") as HTMLElement;

  // Todo lo que no es el modo vive en el modal: aspecto, dorsal, ayuda y plan.
  const customize = createCustomize(root);

  let selected: MatchModeId = "3v3";
  let difficulty: Difficulty = "barrio";

  let players: LocalPlayers = "solo";
  const playersBlock = overlay.querySelector("[data-players-block]") as HTMLElement;
  const soloControls = overlay.querySelector("[data-solo-controls]") as HTMLElement;
  const pairControls = overlay.querySelector("[data-pair-controls]") as HTMLElement;
  // Solo en el navegador y con teclado: en la app o en tactil, ni se enseña.
  playersBlock.hidden = !localPairAvailable();
  const PLAYERS_TITLE: Record<LocalPlayers, string> = {
    solo: t("Tú y tu equipo contra la IA."),
    together: t("Los dos en el mismo equipo contra la IA."),
    versus: t("Uno contra otro, cada uno con su equipo de IA."),
  };
  const syncPlayers = (): void => {
    // En 2v2 no cabe un compañero: juntos no se puede.
    const togetherFits = secondSeat(selected, "together") !== null;
    if (players === "together" && !togetherFits) players = "versus";
    for (const pill of overlay.querySelectorAll<HTMLButtonElement>("[data-players]")) {
      const id = pill.dataset.players as LocalPlayers;
      const on = id === players;
      pill.classList.toggle("is-selected", on);
      pill.setAttribute("aria-checked", on ? "true" : "false");
      pill.disabled = id === "together" && !togetherFits;
      pill.title = pill.disabled ? t("En 2v2 cada equipo tiene un solo jugador de campo") : PLAYERS_TITLE[id];
    }
    // Con dos, el pie enseña las teclas de cada uno en vez de las del menu.
    const pair = players !== "solo" && !playersBlock.hidden;
    soloControls.hidden = pair;
    pairControls.hidden = !pair;
  };
  for (const pill of overlay.querySelectorAll<HTMLButtonElement>("[data-players]")) {
    pill.addEventListener("click", () => {
      const id = pill.dataset.players as LocalPlayers;
      if (!LOCAL_PLAYERS.includes(id)) return;
      players = id;
      syncPlayers();
    });
  }
  syncPlayers();
  const diffBlurb = overlay.querySelector("[data-diff-blurb]") as HTMLElement;
  const selectDifficulty = (d: Difficulty, remember = true): void => {
    difficulty = d;
    if (remember) saveDifficulty(d);
    for (const pill of overlay.querySelectorAll<HTMLElement>("[data-diff]")) {
      const on = pill.dataset.diff === d;
      pill.classList.toggle("is-selected", on);
      pill.setAttribute("aria-checked", on ? "true" : "false");
    }
    diffBlurb.textContent = t(DIFFICULTY_BLURB[d]);
  };
  for (const pill of overlay.querySelectorAll<HTMLElement>("[data-diff]")) {
    pill.addEventListener("click", () => selectDifficulty(pill.dataset.diff as Difficulty));
  }
  let resolvePick: ((result: MenuPick) => void) | null = null;

  for (const id of MATCH_MODE_MENU_IDS) {
    const mode = MATCH_MODES[id];
    const pill = document.createElement("button");
    pill.type = "button";
    pill.className = "mode-pill";
    pill.role = "option";
    pill.dataset.mode = id;
    pill.setAttribute("aria-selected", id === selected ? "true" : "false");
    pill.innerHTML = `<strong>${mode.label}</strong><span>${playersLabel(mode)}</span>`;
    pill.addEventListener("click", () => select(id));
    pill.addEventListener("dblclick", () => {
      select(id);
      confirm();
    });
    modeStrip.appendChild(pill);
  }

  const syncModePreview = (id: MatchModeId): void => {
    const mode = MATCH_MODES[id];
    const pace = Math.round(mode.physics.speedScale * 100);
    previewPitch.innerHTML = pitchDots(mode);
    // `mode.blurb` vive en shared, que no puede importar el i18n del cliente:
    // se traduce aqui, al pintarlo.
    previewMeta.textContent = `${t(mode.blurb)} \u00b7 ${t("ritmo")} ${pace}%`;
  };

  const select = (id: MatchModeId): void => {
    selected = id;
    syncModePreview(id);
    syncPlayers();
    for (const pill of modeStrip.querySelectorAll<HTMLElement>(".mode-pill")) {
      const on = pill.dataset.mode === id;
      pill.classList.toggle("is-selected", on);
      pill.setAttribute("aria-selected", on ? "true" : "false");
    }
  };

  /**
   * Volver al menu principal. Habia solo atajo de teclado (Esc), y en tablet o
   * movil no hay teclado: se entraba aqui y no habia forma de salir.
   */
  function goBack(): void {
    if (!resolvePick) return;
    const done = resolvePick;
    resolvePick = null;
    hide();
    done({ back: true });
  }

  const confirm = (): void => {
    if (!resolvePick) return;
    const settings = customize.current();
    applyTeamPlan(settings.plan);
    applyTeamSetup(settings.team);
    setMatchMode(selected);
    const done = resolvePick;
    resolvePick = null;
    hide();
    done({
      modeId: selected,
      shirtNumber: settings.shirtNumber,
      assist: settings.assist,
      pitchSkin: settings.pitchSkin,
      nickname: settings.nickname,
      difficulty,
      players: playersBlock.hidden ? "solo" : players,
    });
  };

  const openCareer = (): void => {
    if (!resolvePick) return;
    const done = resolvePick;
    resolvePick = null;
    hide();
    done({ career: true });
  };

  const openCustomize = (): void => {
    void customize.open().then(() => {
      playBtn.focus();
    });
  };

  const onKey = (event: KeyboardEvent): void => {
    if (overlay.classList.contains("is-hidden")) return;
    // Con el modal abierto manda el modal: sus teclas no son las del menu.
    if (!document.querySelector(".customize-overlay:not(.is-hidden)")) {
      const idx = MATCH_MODE_MENU_IDS.indexOf(selected);
      if (event.key === "ArrowRight" || event.key === "ArrowDown") {
        event.preventDefault();
        select(MATCH_MODE_MENU_IDS[Math.min(MATCH_MODE_MENU_IDS.length - 1, idx + 1)]!);
      } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
        event.preventDefault();
        select(MATCH_MODE_MENU_IDS[Math.max(0, idx - 1)]!);
      } else if (event.key === "c" || event.key === "C") {
        event.preventDefault();
        openCustomize();
      } else if (event.key === "d" || event.key === "D") {
        // D: la siguiente dificultad (vuelve a empezar tras Primera).
        event.preventDefault();
        selectDifficulty(DIFFICULTIES[(DIFFICULTIES.indexOf(difficulty) + 1) % DIFFICULTIES.length]!);
      } else if (event.key === "Enter" || event.key === " ") {
        // Enter sobre la tarjeta de la carrera es entrar en la carrera, no jugar.
        if (document.activeElement === careerBtn) return;
        event.preventDefault();
        confirm();
      } else if (event.key === "Escape") {
        event.preventDefault();
        goBack();
      }
    }
  };

  playBtn.addEventListener("click", confirm);
  customizeBtn.addEventListener("click", openCustomize);
  careerBtn.addEventListener("click", openCareer);
  window.addEventListener("keydown", onKey);

  let teardownAmbience: (() => void) | null = null;

  const hide = (): void => {
    overlay.classList.add("is-hidden");
    teardownAmbience?.();
    teardownAmbience = null;
  };

  const show = (): Promise<MenuPick> => {
    select(selected);
    careerTeaser.textContent = options.careerTeaser?.() ?? "";
    selectDifficulty(loadDifficulty(options.defaultDifficulty?.() ?? "barrio"), false);
    overlay.classList.remove("is-hidden");
    teardownAmbience = bindMenuAmbience(overlay);
    playBtn.focus();
    return new Promise<MenuPick>((resolve) => {
      resolvePick = resolve;
    });
  };

  select(selected);
  hide();

  return {
    show,
    hide,
    leave: goBack,
    dispose() {
      teardownAmbience?.();
      window.removeEventListener("keydown", onKey);
      customize.dispose();
      overlay.remove();
    },
  };
}
